import axios from 'axios';
import { mockApi } from './mockApi';

/**
 * USE_MOCK_API flag
 * 
 * Set this to true to use localStorage-based mock API (for prototype development)
 * Set this to false to use real API endpoints (when backend is ready)
 * 
 * To switch to real API:
 * 1. Set USE_MOCK_API to false
 * 2. Ensure VITE_API_BASE_URL is set in .env
 * 3. All API calls will automatically use the real backend
 */
const USE_MOCK_API = import.meta.env.VITE_USE_MOCK_API === 'true' || true; // Default to true for prototype

export const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '/api',
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('authToken');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Helper to create API wrapper that switches between mock and real
const createAPIWrapper = <T extends Record<string, (...args: any[]) => Promise<any>>>(
  mockImpl: T,
  realImpl: T
): T => {
  if (USE_MOCK_API) {
    return mockImpl;
  }
  return realImpl;
};

// Patients API - with mock/real switching
const patientsMockAPI = {
  getAll: (params?: {
    page?: number;
    pageSize?: number;
    limit?: number;
    search?: string;
    firstName?: string;
    lastName?: string;
    mrn?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }) => mockApi.get('/patient', { params }),
  getById: (id: string | number) => mockApi.get(`/patient/${id}`),
  create: (data: unknown) => mockApi.post('/patient', data),
  update: (id: string | number, data: unknown) => mockApi.put(`/patient/${id}`, data),
  delete: (id: string | number) => mockApi.delete(`/patient/${id}`),
};

const patientsRealAPI = {
  getAll: (params?: {
    page?: number;
    pageSize?: number;
    limit?: number;
    search?: string;
    firstName?: string;
    lastName?: string;
    mrn?: string;
    sortBy?: string;
    sortDirection?: 'asc' | 'desc';
  }) => api.get('/patients', { params }),
  getById: (id: string | number) => api.get(`/patients/${id}`),
  create: (data: unknown) => api.post('/patients', data),
  update: (id: string | number, data: unknown) => api.put(`/patients/${id}`, data),
  delete: (id: string | number) => api.delete(`/patients/${id}`),
};

export const patientsAPI = createAPIWrapper(patientsMockAPI, patientsRealAPI);

export const messagesAPI = {
  getConversations: () => api.get('/messages/conversations'),
  getMessages: (conversationId: string) => api.get(`/messages/${conversationId}`),
  sendMessage: (data: unknown) => api.post('/messages', data),
};

export const appointmentsAPI = {
  getAll: () => api.get('/appointments'),
  create: (data: unknown) => api.post('/appointments', data),
  update: (id: number, data: unknown) => api.put(`/appointments/${id}`, data),
  delete: (id: number) => api.delete(`/appointments/${id}`),
};

// Organizations API - with mock/real switching
const organizationsMockAPI = {
  getAll: (params?: { page?: number; limit?: number; name?: string; includeDeleted?: boolean }) =>
    mockApi.get('/organization', { params }),
  getById: (id: string) => mockApi.get(`/organization/${id}`),
  create: (data: unknown) => mockApi.post('/organization', data),
  update: (id: string, data: unknown) => mockApi.put(`/organization/${id}`, data),
  delete: (id: string) => mockApi.delete(`/organization/${id}`),
};

const organizationsRealAPI = {
  getAll: (params?: { page?: number; limit?: number; name?: string; includeDeleted?: boolean }) =>
    api.get('/organization', { params }),
  getById: (id: string) => api.get(`/organization/${id}`),
  create: (data: unknown) => api.post('/organization', data),
  update: (id: string, data: unknown) => api.put(`/organization/${id}`, data),
  delete: (id: string) => api.delete(`/organization/${id}`),
};

export const organizationsAPI = createAPIWrapper(organizationsMockAPI, organizationsRealAPI);

// Programs API - with mock/real switching
const programsMockAPI = {
  getAll: (params?: { page?: number; limit?: number; type?: 'global' | 'organization'; organizationId?: string }) =>
    mockApi.get('/program', { params }),
  getById: (id: string) => mockApi.get(`/program/${id}`),
  create: (data: unknown) => mockApi.post('/program', data),
  update: (id: string, data: unknown) => mockApi.put(`/program/${id}`, data),
  delete: (id: string) => mockApi.delete(`/program/${id}`),
};

const programsRealAPI = {
  getAll: (params?: { page?: number; limit?: number; type?: 'global' | 'organization'; organizationId?: string }) =>
    api.get('/program', { params }),
  getById: (id: string) => api.get(`/program/${id}`),
  create: (data: unknown) => api.post('/program', data),
  update: (id: string, data: unknown) => api.put(`/program/${id}`, data),
  delete: (id: string) => api.delete(`/program/${id}`),
};

export const programsAPI = createAPIWrapper(programsMockAPI, programsRealAPI);

// Roles API - with mock/real switching
const rolesMockAPI = {
  getAll: (params?: { page?: number; limit?: number }) =>
    mockApi.get('/role', { params }),
  getById: (id: string) => mockApi.get(`/role/${id}`),
  create: (data: unknown) => mockApi.post('/role', data),
  update: (id: string, data: unknown) => mockApi.put(`/role/${id}`, data),
  delete: (id: string) => mockApi.delete(`/role/${id}`),
};

const rolesRealAPI = {
  getAll: (params?: { page?: number; limit?: number }) =>
    api.get('/role', { params }),
  getById: (id: string) => api.get(`/role/${id}`),
  create: (data: unknown) => api.post('/role', data),
  update: (id: string, data: unknown) => api.put(`/role/${id}`, data),
  delete: (id: string) => api.delete(`/role/${id}`),
};

export const rolesAPI = createAPIWrapper(rolesMockAPI, rolesRealAPI);

// Users API - with mock/real switching
const usersMockAPI = {
  getAll: (params?: { page?: number; limit?: number; organizationId?: string }) =>
    mockApi.get('/user', { params }),
  getById: (id: string) => mockApi.get(`/user/${id}`),
  create: (data: unknown) => mockApi.post('/user', data),
  update: (id: string, data: unknown) => mockApi.put(`/user/${id}`, data),
  delete: (id: string) => mockApi.delete(`/user/${id}`),
};

const usersRealAPI = {
  getAll: (params?: { page?: number; limit?: number; organizationId?: string }) =>
    api.get('/user', { params }),
  getById: (id: string) => api.get(`/user/${id}`),
  create: (data: unknown) => api.post('/user', data),
  update: (id: string, data: unknown) => api.put(`/user/${id}`, data),
  delete: (id: string) => api.delete(`/user/${id}`),
};

export const usersAPI = createAPIWrapper(usersMockAPI, usersRealAPI);

// Devices API - with mock/real switching
const devicesMockAPI = {
  getAll: (params?: { page?: number; limit?: number }) =>
    mockApi.get('/device', { params }),
  getById: (id: string) => mockApi.get(`/device/${id}`),
  create: (data: unknown) => mockApi.post('/device', data),
  update: (id: string, data: unknown) => mockApi.put(`/device/${id}`, data),
  delete: (id: string) => mockApi.delete(`/device/${id}`),
};

const devicesRealAPI = {
  getAll: (params?: { page?: number; limit?: number }) =>
    api.get('/device', { params }),
  getById: (id: string) => api.get(`/device/${id}`),
  create: (data: unknown) => api.post('/device', data),
  update: (id: string, data: unknown) => api.put(`/device/${id}`, data),
  delete: (id: string) => api.delete(`/device/${id}`),
};

export const devicesAPI = createAPIWrapper(devicesMockAPI, devicesRealAPI);

// Measurements API - with mock/real switching
const measurementsMockAPI = {
  getAll: (params?: { patientId?: string | number; type?: string; startDate?: string; endDate?: string }) =>
    mockApi.get('/measurement', { params }),
  getById: (id: string | number) => mockApi.get(`/measurement/${id}`),
  create: (data: unknown) => mockApi.post('/measurement', data),
  update: (id: string | number, data: unknown) => mockApi.put(`/measurement/${id}`, data),
  delete: (id: string | number) => mockApi.delete(`/measurement/${id}`),
};

const measurementsRealAPI = {
  getAll: (params?: { patientId?: string | number; type?: string; startDate?: string; endDate?: string }) =>
    api.get('/measurements', { params }),
  getById: (id: string | number) => api.get(`/measurements/${id}`),
  create: (data: unknown) => api.post('/measurements', data),
  update: (id: string | number, data: unknown) => api.put(`/measurements/${id}`, data),
  delete: (id: string | number) => api.delete(`/measurements/${id}`),
};

export const measurementsAPI = createAPIWrapper(measurementsMockAPI, measurementsRealAPI);

// Device Orders API - with mock/real switching
const deviceOrdersMockAPI = {
  getAll: (params?: { page?: number; limit?: number; status?: string; patientId?: string | number }) =>
    mockApi.get('/device-order', { params }),
  getById: (id: string) => mockApi.get(`/device-order/${id}`),
  create: (data: unknown) => mockApi.post('/device-order', data),
  update: (id: string, data: unknown) => mockApi.put(`/device-order/${id}`, data),
  delete: (id: string) => mockApi.delete(`/device-order/${id}`),
};

const deviceOrdersRealAPI = {
  getAll: (params?: { page?: number; limit?: number; status?: string; patientId?: string | number }) =>
    api.get('/device-order', { params }),
  getById: (id: string) => api.get(`/device-order/${id}`),
  create: (data: unknown) => api.post('/device-order', data),
  update: (id: string, data: unknown) => api.put(`/device-order/${id}`, data),
  delete: (id: string) => api.delete(`/device-order/${id}`),
};

export const deviceOrdersAPI = createAPIWrapper(deviceOrdersMockAPI, deviceOrdersRealAPI);

// Encounters API - with mock/real switching
const encountersMockAPI = {
  getAll: (params?: { patientId?: string | number; status?: string; startDate?: string; endDate?: string }) =>
    mockApi.get('/encounter', { params }),
  getById: (id: string | number) => mockApi.get(`/encounter/${id}`),
  create: (data: unknown) => mockApi.post('/encounter', data),
  update: (id: string | number, data: unknown) => mockApi.put(`/encounter/${id}`, data),
  delete: (id: string | number) => mockApi.delete(`/encounter/${id}`),
};

const encountersRealAPI = {
  getAll: (params?: { patientId?: string | number; status?: string; startDate?: string; endDate?: string }) =>
    api.get('/encounters', { params }),
  getById: (id: string | number) => api.get(`/encounters/${id}`),
  create: (data: unknown) => api.post('/encounters', data),
  update: (id: string | number, data: unknown) => api.put(`/encounters/${id}`, data),
  delete: (id: string | number) => api.delete(`/encounters/${id}`),
};

export const encountersAPI = createAPIWrapper(encountersMockAPI, encountersRealAPI);

// Task Results API - with mock/real switching
const taskResultsMockAPI = {
  getAll: (params?: { patientId?: string | number; taskType?: string; taskName?: string; startDate?: string; endDate?: string }) =>
    mockApi.get('/task-result', { params }),
  getById: (id: string | number) => mockApi.get(`/task-result/${id}`),
  create: (data: unknown) => mockApi.post('/task-result', data),
  update: (id: string | number, data: unknown) => mockApi.put(`/task-result/${id}`, data),
  delete: (id: string | number) => mockApi.delete(`/task-result/${id}`),
};

const taskResultsRealAPI = {
  getAll: (params?: { patientId?: string | number; taskType?: string; taskName?: string; startDate?: string; endDate?: string }) =>
    api.get('/task-results', { params }),
  getById: (id: string | number) => api.get(`/task-results/${id}`),
  create: (data: unknown) => api.post('/task-results', data),
  update: (id: string | number, data: unknown) => api.put(`/task-results/${id}`, data),
  delete: (id: string | number) => api.delete(`/task-results/${id}`),
};

export const taskResultsAPI = createAPIWrapper(taskResultsMockAPI, taskResultsRealAPI);

// Followers API - with mock/real switching
const followersMockAPI = {
  getAll: (params?: { patientId?: string | number }) =>
    mockApi.get('/follower', { params }),
  getById: (id: string | number) => mockApi.get(`/follower/${id}`),
  create: (data: unknown) => mockApi.post('/follower', data),
  update: (id: string | number, data: unknown) => mockApi.put(`/follower/${id}`, data),
  delete: (id: string | number) => mockApi.delete(`/follower/${id}`),
};

const followersRealAPI = {
  getAll: (params?: { patientId?: string | number }) =>
    api.get('/followers', { params }),
  getById: (id: string | number) => api.get(`/followers/${id}`),
  create: (data: unknown) => api.post('/followers', data),
  update: (id: string | number, data: unknown) => api.put(`/followers/${id}`, data),
  delete: (id: string | number) => api.delete(`/followers/${id}`),
};

export const followersAPI = createAPIWrapper(followersMockAPI, followersRealAPI);

// Groups API - with mock/real switching
const groupsMockAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    mockApi.get('/group', { params }),
  getById: (id: string) => mockApi.get(`/group/${id}`),
  create: (data: unknown) => mockApi.post('/group', data),
  update: (id: string, data: unknown) => mockApi.put(`/group/${id}`, data),
  delete: (id: string) => mockApi.delete(`/group/${id}`),
  addUsers: (id: string, userIds: string[]) => mockApi.post(`/group/${id}/users`, { userIds }),
  removeUsers: (id: string, userIds: string[]) => mockApi.delete(`/group/${id}/users`, { data: { userIds } }),
};

const groupsRealAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/group', { params }),
  getById: (id: string) => api.get(`/group/${id}`),
  create: (data: unknown) => api.post('/group', data),
  update: (id: string, data: unknown) => api.put(`/group/${id}`, data),
  delete: (id: string) => api.delete(`/group/${id}`),
  addUsers: (id: string, userIds: string[]) => api.post(`/group/${id}/users`, { userIds }),
  removeUsers: (id: string, userIds: string[]) => api.delete(`/group/${id}/users`, { data: { userIds } }),
};

export const groupsAPI = createAPIWrapper(groupsMockAPI, groupsRealAPI);